import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * DeleteConfirmDialog — reusable destructive-action modal.
 *
 * Two modes:
 *   • Regular (`requireType=false`, default): a plain Cancel / Delete pair.
 *   • Guarded (`requireType=true`): user must literally type `DELETE` before the Delete
 *     button un-disables. Use for bulk operations where a slip is expensive.
 *
 * Backdrop click and Escape both cancel. Delete button is disabled while `busy` so
 * double-clicks can't fire the mutation twice.
 */
export function DeleteConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title = "Delete?",
  description = "This can't be undone.",
  confirmLabel = "Delete",
  requireType = false,
  busy = false,
  testId = "confirm-delete-dialog",
}) {
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);

  // Reset the typed value + focus the input each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setTyped("");
    // Focus after paint so the input is actually mounted.
    const id = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(id);
  }, [open]);

  // Escape closes when the dialog is open — matches the same behaviour as clicking Cancel.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  if (!open) return null;

  const gateOpen = requireType ? typed.trim().toUpperCase() === "DELETE" : true;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8"
      onClick={onCancel}
      data-testid={testId}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md overflow-hidden rounded-lg border border-red-500/30 bg-card shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border/60 bg-red-500/5 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-full bg-red-500/15 p-1.5 text-red-400">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-text" data-testid={`${testId}-title`}>
                {title}
              </h3>
              <p className="mt-1 text-xs text-muted">This action can&apos;t be undone.</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            className="rounded p-1 text-muted hover:bg-elevated hover:text-text"
            data-testid={`${testId}-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-5">
          <p className="text-sm text-text" data-testid={`${testId}-body`}>{description}</p>

          {requireType && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${testId}-typebox`} className="text-[11px] uppercase tracking-widest text-muted">
                Type <span className="font-mono text-red-400">DELETE</span> to confirm
              </label>
              <Input
                id={`${testId}-typebox`}
                ref={inputRef}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="font-mono uppercase"
                data-testid={`${testId}-typebox`}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/60 bg-elevated/30 px-5 py-3">
          <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid={`${testId}-cancel`}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={busy || !gateOpen}
            data-testid={`${testId}-confirm`}
          >
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DeleteConfirmDialog;
